import React, { useRef, useCallback, useState } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Building2, CheckCircle, Users, Settings } from 'lucide-react';
import { ENTITY_TYPE_LABELS } from '@/constants/entityTypes';
import PushBroadcastCard from '@/pages/admin/PushBroadcastCard';
import BroadcastHistoryCard from '@/pages/admin/BroadcastHistoryCard';
import TopPerformersCard from '@/pages/admin/TopPerformersCard';

const DashboardTab = ({ stats }) => {
  // Bridge: BroadcastHistoryCard.onClone -> PushBroadcastCard.cloneFrom;
  // TopPerformersCard.onPromote -> PushBroadcastCard.promoteWeekly;
  // PushBroadcastCard.onBroadcastSaved -> refresh both history + top perf.
  const broadcastRef = useRef(null);
  const historyRef = useRef(null);
  const [topRefreshKey, setTopRefreshKey] = useState(0);

  const handleClone = useCallback((b) => broadcastRef.current?.cloneFrom(b), []);
  const handlePromote = useCallback((b) => broadcastRef.current?.promoteWeekly(b), []);
  const handleSaved = useCallback(() => {
    historyRef.current?.refresh();
    setTopRefreshKey((k) => k + 1);
  }, []);

  return (
    <TabsContent value="dashboard" className="space-y-5 sm:space-y-6">
      {stats ? (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <div className="p-4 sm:p-6 rounded-2xl bg-card border border-border/50">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                <span className="text-xs sm:text-sm text-muted-foreground">Partners</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold">{stats.entities.total}</p>
            </div>
            <div className="p-4 sm:p-6 rounded-2xl bg-card border border-border/50">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" />
                <span className="text-xs sm:text-sm text-muted-foreground">Verified</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold">{stats.entities.verified}</p>
            </div>
            <div className="p-4 sm:p-6 rounded-2xl bg-card border border-border/50">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500" />
                <span className="text-xs sm:text-sm text-muted-foreground">Users</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold">{stats.users.total}</p>
            </div>
            <div className="p-4 sm:p-6 rounded-2xl bg-card border border-border/50">
              <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
                <Settings className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500" />
                <span className="text-xs sm:text-sm text-muted-foreground">Admins</span>
              </div>
              <p className="text-2xl sm:text-3xl font-bold">{stats.users.admins}</p>
            </div>
          </div>

          {/* Entity Breakdown */}
          <div className="p-6 rounded-2xl bg-card border border-border/50">
            <h3 className="text-lg font-semibold mb-4">Partners by Type</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {Object.entries(stats.entities.by_type || {}).map(([type, count]) => (
                <div key={type} className="p-3 rounded-xl bg-muted/50">
                  <p className="text-sm text-muted-foreground">{ENTITY_TYPE_LABELS[type] || type}</p>
                  <p className="text-2xl font-bold">{count}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Push Notification Broadcast + Top Performers + History */}
          <PushBroadcastCard
            ref={broadcastRef}
            enabled={true}
            onBroadcastSaved={handleSaved}
          />
          <TopPerformersCard
            enabled={true}
            onPromote={handlePromote}
            refreshKey={topRefreshKey}
          />
          <BroadcastHistoryCard
            ref={historyRef}
            enabled={true}
            onClone={handleClone}
          />
        </>
      ) : (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </TabsContent>
  );
};

export default DashboardTab;
